import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { X, Minus, Plus, ForkKnife, Bag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import useSiteSettings from "@/hooks/useSiteSettings";

export default function CartDrawer() {
    const { items, setQty, remove, subtotal, open, setOpen, clear } = useCart();
    const { t } = useI18n();
    const { settings } = useSiteSettings();
    const isOpenForOrders = settings.is_open;
    const deliveryEnabled = settings.delivery_enabled !== false;
    const [fulfillment, setFulfillment] = useState("pickup");

    // If the owner turns delivery off while the cart is open, fall back to pickup.
    useEffect(() => {
        if (!deliveryEnabled && fulfillment === "delivery") {
            setFulfillment("pickup");
        }
    }, [deliveryEnabled, fulfillment]);
    const [form, setForm] = useState({
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        address: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const { customer } = useCustomer();

    // Prefill checkout fields from the signed-in customer account.
    useEffect(() => {
        if (!customer) return;
        setForm((prev) => ({
            ...prev,
            customer_name: prev.customer_name || customer.name || "",
            customer_phone: prev.customer_phone || customer.phone || "",
            customer_email: prev.customer_email || customer.email || "",
            address: prev.address || customer.address || "",
        }));
    }, [customer]);

    const [tipPct, setTipPct] = useState(0); // 0 | 10 | 15 | 20 | "custom"
    const [tipCustom, setTipCustom] = useState("");
    const deliveryFee = fulfillment === "delivery" ? 3.99 : 0;
    const taxableSubtotal = items.reduce((s, i) => s + (i.taxable === false ? 0 : Number(i.price) * i.qty), 0);
    const tax = +(taxableSubtotal * 0.0735).toFixed(2);
    const tipAmt = tipPct === "custom"
        ? Math.max(0, parseFloat(tipCustom) || 0)
        : +((subtotal * tipPct) / 100).toFixed(2);
    const total = +(subtotal + deliveryFee + tax + tipAmt).toFixed(2);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const checkout = async () => {
        if (items.length === 0) return;
        if (!isOpenForOrders) {
            toast.error("Sorry — we're closed right now. Try again during open hours.");
            return;
        }
        if (!form.customer_name || !form.customer_phone) {
            toast.error("Name and phone are required");
            return;
        }
        if (fulfillment === "delivery" && !form.address) {
            toast.error("Delivery address required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                items: items.map((i) => ({ id: i.id, qty: i.qty })),
                fulfillment,
                origin_url: window.location.origin,
                tip: +tipAmt.toFixed(2),
                customer_name: form.customer_name,
                customer_phone: form.customer_phone,
                ...(form.customer_email ? { customer_email: form.customer_email } : {}),
                ...(form.address ? { address: form.address } : {}),
                ...(form.notes ? { notes: form.notes } : {}),
            };
            const res = await api.post("/checkout", payload);
            if (res.data?.url) {
                clear();
                window.location.href = res.data.url;
            } else {
                toast.error("Couldn't start checkout");
            }
        } catch (e) {
            const d = e?.response?.data?.detail;
            const msg = Array.isArray(d)
                ? d.map((x) => x.msg).join(", ")
                : d || "Checkout error";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                side="right"
                data-testid="cart-drawer"
                className="w-full sm:max-w-md flex flex-col p-0"
            >
                <SheetHeader className="px-6 pt-6">
                    <SheetTitle className="font-display text-2xl font-black tracking-tight">
                        {t("cart.title")}
                    </SheetTitle>
                </SheetHeader>

                <div className="px-6 pb-4">
                    <div
                        role="tablist"
                        className="grid grid-cols-2 bg-muted rounded-full p-1 mt-2"
                    >
                        {[
                            { id: "pickup", icon: <Bag size={16} weight="bold" />, disabled: false },
                            { id: "delivery", icon: <ForkKnife size={16} weight="bold" />, disabled: !deliveryEnabled },
                        ].map((opt) => (
                            <button
                                key={opt.id}
                                data-testid={`fulfillment-${opt.id}`}
                                onClick={() => {
                                    if (opt.disabled) return;
                                    setFulfillment(opt.id);
                                }}
                                disabled={opt.disabled}
                                className={`flex items-center justify-center gap-2 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition ${
                                    opt.disabled
                                        ? "text-foreground/25 cursor-not-allowed line-through"
                                        : fulfillment === opt.id
                                        ? "bg-foreground text-background"
                                        : "text-foreground/60"
                                }`}
                            >
                                {opt.icon}
                                {t(`cart.${opt.id}`)}
                            </button>
                        ))}
                    </div>
                    {!deliveryEnabled && (
                        <p
                            data-testid="delivery-paused-banner"
                            className="mt-2 text-[11px] text-center font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"
                        >
                            {t("cart.delivery_paused") || "Delivery paused — pickup only"}
                        </p>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-4">
                    {items.length === 0 ? (
                        <div
                            data-testid="cart-empty"
                            className="text-center py-16 text-muted-foreground"
                        >
                            <ForkKnife size={48} weight="duotone" className="mx-auto mb-4 opacity-40" />
                            <p>{t("cart.empty")}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {items.map((it) => (
                                <div
                                    key={it.id}
                                    data-testid={`cart-line-${it.id}`}
                                    className="flex gap-3 bg-muted/50 rounded-xl p-3"
                                >
                                    <img
                                        src={it.image}
                                        alt={it.name}
                                        className="w-16 h-16 rounded-lg object-cover"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm truncate">{it.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            ${it.price.toFixed(2)}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <button
                                                data-testid={`qty-dec-${it.id}`}
                                                onClick={() => setQty(it.id, it.qty - 1)}
                                                className="h-7 w-7 grid place-items-center rounded-full bg-white border border-border hover:bg-foreground hover:text-background transition"
                                            >
                                                <Minus size={12} weight="bold" />
                                            </button>
                                            <span className="text-sm font-bold w-6 text-center">{it.qty}</span>
                                            <button
                                                data-testid={`qty-inc-${it.id}`}
                                                onClick={() => setQty(it.id, it.qty + 1)}
                                                className="h-7 w-7 grid place-items-center rounded-full bg-white border border-border hover:bg-foreground hover:text-background transition"
                                            >
                                                <Plus size={12} weight="bold" />
                                            </button>
                                            <button
                                                data-testid={`remove-${it.id}`}
                                                onClick={() => remove(it.id)}
                                                className="ml-auto h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-destructive transition"
                                            >
                                                <X size={14} weight="bold" />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="font-display font-black text-sm">
                                        ${(it.price * it.qty).toFixed(2)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {items.length > 0 && (
                        <div className="mt-5 space-y-3">
                            <Input
                                name="customer_name"
                                placeholder={t("cart.name")}
                                value={form.customer_name}
                                onChange={onChange}
                                data-testid="cart-name"
                            />
                            <Input
                                name="customer_phone"
                                placeholder={t("cart.phone")}
                                value={form.customer_phone}
                                onChange={onChange}
                                data-testid="cart-phone"
                            />
                            <Input
                                name="customer_email"
                                type="email"
                                placeholder={t("cart.email")}
                                value={form.customer_email}
                                onChange={onChange}
                                data-testid="cart-email"
                            />
                            {fulfillment === "delivery" && (
                                <Input
                                    name="address"
                                    placeholder={t("cart.address")}
                                    value={form.address}
                                    onChange={onChange}
                                    data-testid="cart-address"
                                />
                            )}
                            <Textarea
                                name="notes"
                                placeholder={t("cart.notes")}
                                rows={2}
                                value={form.notes}
                                onChange={onChange}
                                data-testid="cart-notes"
                            />
                        </div>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-border bg-white px-6 py-5 space-y-2">
                        {!isOpenForOrders && (
                            <div
                                data-testid="closed-banner"
                                className="bg-red-50 border border-red-200 text-red-900 rounded-xl p-3 text-xs font-bold uppercase tracking-widest text-center"
                            >
                                We're closed right now — orders are paused.
                            </div>
                        )}
                        <Row label={t("cart.subtotal")} value={`$${subtotal.toFixed(2)}`} testid="row-subtotal" />
                        <Row
                            label={t("cart.delivery_fee")}
                            value={fulfillment === "delivery" ? `$${deliveryFee.toFixed(2)}` : t("cart.free_pickup")}
                            testid="row-delivery"
                        />
                        <Row label={`${t("cart.tax")} 7.35%`} value={`$${tax.toFixed(2)}`} testid="row-tax" />
                        <div className="py-1" data-testid="tip-selector">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">
                                {t("cart.tip") || "Tip / Propina"}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {[0, 10, 15, 20, "custom"].map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        data-testid={`tip-${p}`}
                                        onClick={() => setTipPct(p)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${
                                            tipPct === p
                                                ? "bg-foreground text-background border-foreground"
                                                : "bg-white border-border hover:border-foreground"
                                        }`}
                                    >
                                        {p === 0 ? "No tip" : p === "custom" ? "Other" : `${p}%`}
                                    </button>
                                ))}
                                {tipPct === "custom" && (
                                    <Input
                                        data-testid="tip-custom-input"
                                        type="number" min="0" step="0.01" inputMode="decimal"
                                        value={tipCustom}
                                        onChange={(e) => setTipCustom(e.target.value)}
                                        placeholder="$"
                                        className="h-8 w-24"
                                    />
                                )}
                            </div>
                        </div>
                        {tipAmt > 0 && <Row label={t("cart.tip") || "Tip"} value={`$${tipAmt.toFixed(2)}`} testid="row-tip" />}
                        <Row
                            label={t("cart.total")}
                            value={`$${total.toFixed(2)}`}
                            big
                            testid="row-total"
                        />
                        <Button
                            data-testid="checkout-btn"
                            onClick={checkout}
                            disabled={busy || !isOpenForOrders}
                            className="w-full mt-2 py-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {busy ? "..." : !isOpenForOrders ? "Closed" : t("cart.checkout")}
                        </Button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function Row({ label, value, big, testid }) {
    return (
        <div className="flex items-center justify-between" data-testid={testid}>
            <span className={big ? "font-display font-black text-xl" : "text-sm text-muted-foreground"}>
                {label}
            </span>
            <span className={big ? "font-display font-black text-2xl" : "text-sm font-semibold"}>
                {value}
            </span>
        </div>
    );
}
